import { Modal } from 'components/Modal';
import { Title } from 'components/Title';
import { FC, useState, useEffect } from 'react';
import {
  useGetDocumentTypesQuery,
  useGenerateDocsSetMutation,
} from 'redux/Api/ordersApi/ordersApi';
import { Button } from 'ui/Button';
import { ReactComponent as Checkbox } from './img/iconCheckbox.svg';
import { ReactComponent as DownloadIcon } from './img/iconDownload.svg';
import styles from './OrdersDownloadModal.module.scss';
import { Loader } from '@mantine/core';
import { send } from 'process';
import useYandexMetrica from 'hooks/useYandexMetrica';

interface OrdersDownloadModalTypes {
  setShow: (e: boolean) => void;
  show: boolean;
  selectedOrders?: string[];
  onGenerateDocuments?: (fileId: string) => void;
  filters?: any;
}

interface DocumentType {
  name: string;
  label: string;
}

export const OrdersDownloadModal: FC<OrdersDownloadModalTypes> = ({
  setShow,
  show,
  selectedOrders = [],
  onGenerateDocuments,
  filters,
}) => {
  const { data: documentTypesData, isLoading: isLoadingDocTypes } =
    useGetDocumentTypesQuery();
  const [generateDocsSet, { isLoading: isGeneratingDocs }] = useGenerateDocsSetMutation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<Record<string, boolean>>({});
  const { sendGoal } = useYandexMetrica();

  useEffect(() => {
    if (documentTypesData && Array.isArray(documentTypesData)) {
      const initialState: Record<string, boolean> = {};
      documentTypesData.forEach((docType: DocumentType) => {
        initialState[docType.name] = false;
      });
      setSelectedDocuments(initialState);
    }
  }, [documentTypesData]);

  const handleToggleDocument = (key: string) => {
    setSelectedDocuments((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleGenerateDocuments = async () => {
    setIsDownloading(true);
    const selectedTypes = Object.entries(selectedDocuments)
      .filter(([_, isSelected]) => isSelected)
      .map(([type, _]) => type);

    try {
      const requestBody: any = {
        types: selectedTypes,
      };

      if (selectedOrders.length === 0 && filters) {
        requestBody.filters = filters;
      } else {
        requestBody.items = selectedOrders;
      }

      const result = await generateDocsSet(requestBody).unwrap();

      if (result.file_id && onGenerateDocuments) {
        onGenerateDocuments(String(result.file_id));
        setShow(false);
      }
    } catch (error) {
      console.error('Ошибка при генерации документов:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoadingDocTypes) {
    return (
      <Modal setOpen={setShow} open={show}>
        <div className={styles.root}>
          <div>
            <Title title='Скачать документы' />
          </div>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            Загрузка типов документов...
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal setOpen={setShow} open={show}>
      <div className={styles.root}>
        <div>
          <Title title='Скачать документы' />
        </div>
        <span className={styles.root_subtitle}>
          Мы сформируем ZIP-архив с выбранными документами
        </span>
        <div className={styles.main}>
          {documentTypesData && Array.isArray(documentTypesData) ? (
            documentTypesData.map((docType: DocumentType) => (
              <div
                key={docType.name}
                className={styles.main_item}
                onClick={() => handleToggleDocument(docType.name)}>
                {selectedDocuments[docType.name] ? (
                  <div className={styles.checkbox_active}>
                    <Checkbox />
                  </div>
                ) : (
                  <div className={styles.checkbox}></div>
                )}{' '}
                {docType.label}
              </div>
            ))
          ) : (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              Не удалось загрузить типы документов
            </div>
          )}
        </div>

        <Button
          onClick={() => {
            handleGenerateDocuments();
            sendGoal('GenerateDocuments');
          }}
          className={styles.btn}
          disabled={isGeneratingDocs || !Object.values(selectedDocuments).some(Boolean)}
          theme='filed'>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
            }}>
            Сформировать
            <div style={{ width: '24px', height: '24px' }}>
              {isDownloading ? (
                <Loader size='xs' color='white' />
              ) : (
                <DownloadIcon style={{ width: '24px', height: '24px', margin: 0 }} />
              )}
            </div>
          </div>
        </Button>
      </div>
    </Modal>
  );
};
